import React, { useState, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  Polygon,
  Marker,
  Popup,
  useMapEvents,
  Circle,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Typography, Row, Col, Card, Tag, Grid } from "antd";
import { EnvironmentOutlined } from "@ant-design/icons";
import { TbPolygon, TbCircle } from "react-icons/tb";
import { LocationIcons } from "./LocationIcons";
import GeoFencesList from "./GeoFencesList";
import CreateGeoFence from "./CreateGeoFence";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const DEFAULT_CENTER = [13.0827, 80.2707];
const LOCAL_STORAGE_KEY = "savedGeofences";

const MOCK_GEOFENCES = [
  {
    id: "5c7fdb6a-1a54-4cd0-9d46-76d4ec9e6920",
    name: "Office Zone One",
    type: "polygon",
    vehicleKey: "VEHICLE_001",
    description: "Visteon office perimeter",
    status: "active",
    createdAt: "2025-07-11T10:51:40.475Z",
    updatedAt: "2025-07-11T10:51:40.475Z",
    coordinates: [
      { lat: 37.421999, lng: -122.084015 },
      { lat: 37.421999, lng: -122.083015 },
      { lat: 37.420999, lng: -122.083015 },
      { lat: 37.420999, lng: -122.084015 },
      { lat: 37.421999, lng: -122.084015 },
    ],
  },
  {
    id: "f964736b-c787-45e6-93da-1bff6f515053",
    name: "Office Zone Two",
    type: "circle",
    vehicleKey: "VEHICLE_001",
    description: "Visteon campus radius",
    status: "inactive",
    createdAt: "2025-07-11T10:37:51.624Z",
    updatedAt: "2025-07-11T10:37:51.624Z",
    center: { lat: 13.0045577453397811, lng: 80.2195312500001 },
    radius: 500,
  },
  {
    id: "f964736b-c787-45e6-93da-1bff6f515054",
    name: "Office Zone Three",
    type: "circle",
    vehicleKey: "VEHICLE_001",
    description: "Extended campus zone",
    status: "active",
    createdAt: "2025-07-11T10:37:51.624Z",
    updatedAt: "2025-07-11T10:37:51.624Z",
    center: { lat: 12.98799858811665, lng: 80.0427818298339 },
    radius: 1000,
  },
];

// ─── Map event helpers ────────────────────────────────────────────────────────
function PolygonPlotter({ onAddPoint }) {
  useMapEvents({ click(e) { onAddPoint([e.latlng.lat, e.latlng.lng]); } });
  return null;
}

function CirclePlotter({ onSetCenter }) {
  useMapEvents({ click(e) { onSetCenter([e.latlng.lat, e.latlng.lng]); } });
  return null;
}

function PanToView({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.setView(position, map.getZoom(), { animate: true });
  }, [position, map]);
  return null;
}

function getFenceCenter(fence) {
  if (fence.type === "circle") return fence.center;
  if (fence?.points?.length) {
    const pts = fence.points;
    return [
      pts.reduce((s, p) => s + p[0], 0) / pts.length,
      pts.reduce((s, p) => s + p[1], 0) / pts.length,
    ];
  }
  if (fence?.coordinates?.length) {
    const coords = fence.coordinates;
    return [
      coords.reduce((s, p) => s + p.lat, 0) / coords.length,
      coords.reduce((s, p) => s + p.lng, 0) / coords.length,
    ];
  }
}

const getCenterPoint = (center) => [center.lat, center.lng];
const getPolygonPoints = (coords) => coords.map((p) => [p.lat, p.lng]);

const DrawingHint = ({ fenceType, polygonPoints, circleCenter }) => {
  const text =
    fenceType === "polygon"
      ? polygonPoints.length > 0
        ? `${polygonPoints.length} point${polygonPoints.length !== 1 ? "s" : ""} — click map to add more (min 3)`
        : "Click on the map to start placing polygon points"
      : circleCenter
        ? "Center placed — adjust radius in the panel and save"
        : "Click on the map to place the circle center";

  const isPolygon = fenceType === "polygon";
  return (
    <div
      style={{
        position: "absolute",
        top: 12,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1000,
        background: isPolygon ? "rgba(124,58,237,0.9)" : "rgba(249,115,22,0.9)",
        backdropFilter: "blur(6px)",
        color: "#fff",
        padding: "5px 16px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        pointerEvents: "none",
        whiteSpace: "nowrap",
        display: "flex",
        alignItems: "center",
        gap: 6,
        boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
      }}
    >
      <span style={{ fontSize: 14 }}>
        {isPolygon ? <TbPolygon /> : <TbCircle />}
      </span>
      {text}
    </div>
  );
};

const Geofencing = ({ vehicle }) => {
  const screens = useBreakpoint();
  const isMobile = screens.md === false;

  const [fenceType, setFenceType] = useState("polygon");
  const [polygonPoints, setPolygonPoints] = useState([]);
  const [circleCenter, setCircleCenter] = useState(null);
  const [circleRadius, setCircleRadius] = useState(500);
  const [savedFences, setSavedFences] = useState([]);
  const [panTo, setPanTo] = useState(null);
  const [geofences, setGeofences] = useState(MOCK_GEOFENCES);

  // Load / save local fences
  useEffect(() => {
    const data = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (data) {
      try { setSavedFences(JSON.parse(data)); } catch { setSavedFences([]); }
    }
  }, []);
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(savedFences));
  }, [savedFences]);

  const handleAddPolygonPoint = (latlng) => setPolygonPoints((p) => [...p, latlng]);
  const handleSetCircleCenter = (latlng) => setCircleCenter(latlng);
  const handlePanToView = (idx) => setPanTo(getFenceCenter(savedFences[idx]));
  const handlePanToViewForGeofences = (fence) => setPanTo(getFenceCenter(fence));

  // ── derived stats ──────────────────────────────────────────────────────────
  const activeCount = geofences.filter((f) => f.status === "active").length;
  const inactiveCount = geofences.length - activeCount;
  const mapHeight = isMobile ? 280 : 450;

  const activePath = { color: "#2563eb", fillColor: "#2563eb", fillOpacity: 0.10, weight: 2 };
  const inactivePath = { color: "#94a3b8", fillColor: "#94a3b8", fillOpacity: 0.05, weight: 1.5, dashArray: "5 4" };
  const savedPath = { color: "#10b981", fillColor: "#10b981", fillOpacity: 0.12, weight: 2 };
  const drawPolyPath = { color: "#7c3aed", fillColor: "#7c3aed", fillOpacity: 0.12, weight: 2, dashArray: "6 4" };
  const drawCircPath = { color: "#f97316", fillColor: "#f97316", fillOpacity: 0.10, weight: 2, dashArray: "6 4" };

  return (
    <>
      <div
        style={{
          display: "flex", alignItems: "flex-start", gap: 10,
          marginBottom: 20, flexWrap: "wrap", justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 38, height: 38, borderRadius: 10,
              background: "#ecfdf5", border: "1px solid #6ee7b7",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}
          >
            <EnvironmentOutlined style={{ fontSize: 18, color: "#059669" }} />
          </div>
          <div>
            <Title level={4} style={{ margin: 0 }}>Geofencing</Title>
            <Text style={{ fontSize: 12, color: "#94a3b8" }}>
              Draw and manage geofence zones for this vehicle
            </Text>
          </div>
        </div>

        {/* stat chips */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div
            style={{
              padding: "5px 14px", borderRadius: 20,
              background: "#eff6ff", border: "1px solid #bfdbfe",
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, color: "#2563eb" }}>
              {geofences.length} Zone{geofences.length !== 1 ? "s" : ""}
            </span>
          </div>
          <Tag color="success" style={{ borderRadius: 20, fontSize: 11, padding: "2px 10px", margin: 0 }}>
            {activeCount} Active
          </Tag>
          <Tag color="default" style={{ borderRadius: 20, fontSize: 11, padding: "2px 10px", margin: 0 }}>
            {inactiveCount} Inactive
          </Tag>
        </div>
      </div>

      <Row gutter={[16, 16]}>

        {/* Map card */}
        <Col xs={24} lg={16} style={{ display: "flex" }}>
          <Card
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>Live Map</span>
                <Tag
                  style={{
                    borderRadius: 20, fontSize: 10, fontWeight: 600, margin: 0,
                    background: fenceType === "polygon" ? "#f5f3ff" : "#fff7ed",
                    color: fenceType === "polygon" ? "#7c3aed" : "#c2410c",
                    border: `1px solid ${fenceType === "polygon" ? "#c4b5fd" : "#fed7aa"}`,
                  }}
                >
                  {fenceType === "polygon" ? "⬡ Polygon" : "◎ Circle"} Mode
                </Tag>
              </div>
            }
            style={{ borderRadius: 12, border: "1px solid #e2e8f0", width: "100%" }}
            styles={{ body: { padding: 0 } }}
          >
            <div style={{ position: "relative", borderRadius: "0 0 10px 10px", overflow: "hidden" }}>
              <DrawingHint
                fenceType={fenceType}
                polygonPoints={polygonPoints}
                circleCenter={circleCenter}
              />

              <MapContainer
                center={panTo || DEFAULT_CENTER}
                zoom={13}
                style={{ height: mapHeight, width: "100%" }}
              >
                {panTo && <PanToView position={panTo} />}
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="&copy; OpenStreetMap contributors"
                />

                {/* drawing listeners */}
                {fenceType === "polygon" && (
                  <PolygonPlotter onAddPoint={handleAddPolygonPoint} />
                )}
                {fenceType === "circle" && (
                  <CirclePlotter onSetCenter={handleSetCircleCenter} />
                )}

                {/* in-progress polygon */}
                {fenceType === "polygon" && polygonPoints.length > 2 && (
                  <Polygon positions={polygonPoints} pathOptions={drawPolyPath}>
                    <Popup>Drawing — {polygonPoints.length} points placed</Popup>
                  </Polygon>
                )}
                {fenceType === "polygon" &&
                  polygonPoints.map((pt, i) => (
                    <Marker key={i} position={pt} icon={LocationIcons.placeIcon}>
                      <Popup>Point {i + 1}: {pt[0].toFixed(5)}, {pt[1].toFixed(5)}</Popup>
                    </Marker>
                  ))}

                {/* in-progress circle */}
                {fenceType === "circle" && circleCenter && (
                  <>
                    <Circle center={circleCenter} radius={circleRadius} pathOptions={drawCircPath}>
                      <Popup>Radius: {circleRadius} m</Popup>
                    </Circle>
                    <Marker position={circleCenter} icon={LocationIcons.placeIcon}>
                      <Popup>
                        Center: {circleCenter[0].toFixed(5)}, {circleCenter[1].toFixed(5)}
                      </Popup>
                    </Marker>
                  </>
                )}

                {/* locally saved fences (green) */}
                {savedFences.map((fence, i) =>
                  fence.type === "polygon" ? (
                    <Polygon key={`ls-${i}`} positions={fence.points} pathOptions={savedPath}>
                      <Popup>{fence.name || `Saved Zone #${i + 1}`}</Popup>
                    </Polygon>
                  ) : (
                    <Circle key={`ls-${i}`} center={fence.center} radius={fence.radius} pathOptions={savedPath}>
                      <Popup>{fence.name || `Saved Zone #${i + 1}`} — {fence.radius}m</Popup>
                    </Circle>
                  )
                )}

                {/* server geofences (blue = active, grey dashed = inactive) */}
                {geofences.map((fence) => {
                  const path = fence.status === "active" ? activePath : inactivePath;
                  return fence.type === "polygon" ? (
                    <Polygon
                      key={fence.id}
                      positions={getPolygonPoints(fence.coordinates)}
                      pathOptions={path}
                    >
                      <Popup>
                        <strong>{fence.name}</strong>
                        <br />{fence.type} · {fence.status}
                        {fence.description && <><br />{fence.description}</>}
                      </Popup>
                    </Polygon>
                  ) : (
                    <Circle
                      key={fence.id}
                      center={getCenterPoint(fence.center)}
                      radius={fence.radius}
                      pathOptions={path}
                    >
                      <Popup>
                        <strong>{fence.name}</strong>
                        <br />{fence.radius}m radius · {fence.status}
                        {fence.description && <><br />{fence.description}</>}
                      </Popup>
                    </Circle>
                  );
                })}
              </MapContainer>
            </div>
          </Card>
        </Col>

        {/* Create zone panel */}
        <Col xs={24} lg={8} style={{ display: "flex" }}>
          <CreateGeoFence
            vehicle={vehicle}
            handlePanToView={handlePanToView}
            polygonPoints={polygonPoints}
            setPolygonPoints={setPolygonPoints}
            fenceType={fenceType}
            setFenceType={setFenceType}
            circleCenter={circleCenter}
            setCircleCenter={setCircleCenter}
            circleRadius={circleRadius}
            setCircleRadius={setCircleRadius}
            savedFences={savedFences}
            setSavedFences={setSavedFences}
          />
        </Col>
      </Row>

      <div style={{ marginTop: 16 }}>
        <GeoFencesList
          vehicle={vehicle}
          handlePanToViewForGeofences={handlePanToViewForGeofences}
          geofences={geofences}
          setGeofences={setGeofences}
        />
      </div>
    </>
  );
};

export default Geofencing;
